import React from 'react';
import { Heart, Award, Users, Clock, Shield, MapPin } from 'lucide-react';
import { businessInfo, whyChooseUs, faqs } from '../mockData';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';

const About = () => {
  const aboutImage = "https://images.pexels.com/photos/546819/pexels-photo-546819.jpeg";
  const workspaceImage = "https://images.pexels.com/photos/356056/pexels-photo-356056.jpeg";

  return (
    <div className="min-h-screen bg-black pt-24 pb-16">
      {/* Hero Section */}
      <section className="py-12 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-white/5 to-transparent"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h1 className="text-4xl lg:text-5xl font-bold text-white mb-4">
              About Evolution Cell Phone Repairs
            </h1>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              Your trusted phone repair experts in El Paso, Texas
            </p>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <img 
                src={aboutImage}
                alt="Professional repair workspace"
                className="rounded-2xl shadow-2xl border border-neutral-800"
              />
            </div>
            <div>
              <h2 className="text-3xl font-bold text-white mb-6">
                Serving El Paso with Pride
              </h2>
              <div className="space-y-4 text-gray-300 leading-relaxed">
                <p>
                  Evolution Cell Phone Repairs has been serving the El Paso community with fast, affordable, and reliable device repair services. We understand how important your devices are to your daily life, which is why we're committed to getting them back to you as quickly as possible.
                </p>
                <p>
                  Our team of certified technicians brings years of experience and expertise to every repair. Whether it's a cracked phone screen, a laptop that won't boot, a gaming console overheating, or a desktop computer running slow, we've seen it all and fixed it all.
                </p>
                <p>
                  We specialize in repairing smartphones, laptops, desktop computers, and game consoles (PlayStation, Xbox, Nintendo Switch). From screen replacements to water damage recovery, hardware upgrades to virus removal - we handle it all with professional care.
                </p>
                <p>
                  As a proud <span className="text-white font-semibold">Latino-owned</span> and <span className="text-pink-400 font-semibold">LGBTQ+ friendly</span> business, we believe in creating a welcoming environment for everyone in our community. We're here to serve you with respect, professionalism, and care.
                </p>
              </div>

              <div className="mt-8 flex flex-wrap gap-3">
                <div className="px-4 py-2 bg-white/10 border border-white/20 rounded-lg">
                  <p className="text-white font-semibold">Latino-Owned Business</p>
                </div>
                <div className="px-4 py-2 bg-pink-500/10 border border-pink-500/20 rounded-lg flex items-center gap-2">
                  <Heart className="w-4 h-4 text-pink-400" />
                  <p className="text-pink-400 font-semibold">LGBTQ+ Friendly</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-16 bg-neutral-900/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
              What Makes Us Different
            </h2>
            <p className="text-gray-400 text-lg">
              Our commitment to quality, speed, and customer satisfaction
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {whyChooseUs.map((item, index) => {
              const icons = {
                Award, Shield, Clock, Heart
              };
              const iconName = item.icon;
              const IconComponent = icons[iconName] || Award;

              return (
                <div 
                  key={index}
                  className="p-6 bg-neutral-900 border border-neutral-800 rounded-xl hover:border-white/50 transition-all"
                >
                  <div className="w-12 h-12 bg-white/10 rounded-lg flex items-center justify-center text-white mb-4">
                    <IconComponent className="w-6 h-6" />
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-2">{item.title}</h3>
                  <p className="text-gray-400">{item.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Workspace Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-white mb-6">
                State-of-the-Art Equipment
              </h2>
              <p className="text-gray-300 leading-relaxed mb-6">
                Our repair shop is equipped with professional-grade tools and technology to ensure every repair meets the highest standards. We invest in the latest equipment and training to stay ahead in the fast-evolving world of mobile device repair.
              </p>
              <ul className="space-y-3">
                <li className="flex items-center gap-3 text-gray-300">
                  <div className="w-2 h-2 bg-white rounded-full"></div>
                  Professional repair tools and equipment
                </li>
                <li className="flex items-center gap-3 text-gray-300">
                  <div className="w-2 h-2 bg-white rounded-full"></div>
                  Clean, organized workspace
                </li>
                <li className="flex items-center gap-3 text-gray-300">
                  <div className="w-2 h-2 bg-white rounded-full"></div>
                  Quality replacement parts inventory
                </li>
                <li className="flex items-center gap-3 text-gray-300">
                  <div className="w-2 h-2 bg-white rounded-full"></div>
                  Advanced diagnostic equipment
                </li>
              </ul>
            </div>
            <div>
              <img 
                src={workspaceImage}
                alt="Repair workspace"
                className="rounded-2xl shadow-2xl border border-neutral-800"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-neutral-900/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="text-center p-6 bg-neutral-900 border border-neutral-800 rounded-xl">
              <p className="text-4xl font-bold text-white mb-2">270+</p>
              <p className="text-gray-400">Happy Customers</p>
            </div>
            <div className="text-center p-6 bg-neutral-900 border border-neutral-800 rounded-xl">
              <p className="text-4xl font-bold text-white mb-2">4.1</p>
              <p className="text-gray-400">Star Rating</p>
            </div>
            <div className="text-center p-6 bg-neutral-900 border border-neutral-800 rounded-xl">
              <p className="text-4xl font-bold text-white mb-2">Same Day</p>
              <p className="text-gray-400">Service</p>
            </div>
            <div className="text-center p-6 bg-neutral-900 border border-neutral-800 rounded-xl">
              <p className="text-4xl font-bold text-white mb-2">2AM</p>
              <p className="text-gray-400">Open Until</p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-gray-400 text-lg">
              Got questions? We've got answers.
            </p>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem 
                key={index} 
                value={`item-${index}`}
                className="bg-neutral-900 border border-neutral-800 rounded-xl px-6 overflow-hidden"
              >
                <AccordionTrigger className="text-left text-white hover:text-white py-4">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-gray-300 pb-4">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Location Section */}
      <section className="py-16 bg-neutral-900/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
              Visit Us in El Paso
            </h2>
            <p className="text-gray-400 text-lg">
              Convenient location with easy parking
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-neutral-900 border border-neutral-800 rounded-xl p-8">
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <MapPin className="w-6 h-6 text-white flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-white font-semibold mb-1">Address</h3>
                    <p className="text-gray-300">{businessInfo.address}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <Clock className="w-6 h-6 text-white flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-white font-semibold mb-1">Hours</h3>
                    <p className="text-gray-300">Open Daily</p>
                    <p className="text-white font-medium">Closes at 2:00 AM</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <Users className="w-6 h-6 text-white flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-white font-semibold mb-1">Walk-Ins Welcome</h3>
                    <p className="text-gray-300">No appointment needed, but calling ahead helps reduce wait times</p>
                  </div>
                </div>
              </div>
              <div className="mt-8">
                <a
                  href={`https://maps.google.com/?q=${encodeURIComponent(businessInfo.address)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 w-full px-6 py-3 bg-white hover:bg-gray-100 text-black font-semibold rounded-lg transition-colors"
                >
                  <MapPin className="w-5 h-5" />
                  Get Directions
                </a>
              </div>
            </div>

            <div className="bg-neutral-900 border border-neutral-800 rounded-xl overflow-hidden">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3397.4!2d-106.3!3d31.8!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMzHCsDQ4JzAwLjAiTiAxMDbCsDE4JzAwLjAiVw!5e0!3m2!1sen!2sus!4v1234567890"
                width="100%"
                height="100%"
                style={{ border: 0, minHeight: '400px' }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Evolution Cell Phone Repairs Location"
              ></iframe>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
